import { onMounted, onBeforeUnmount, ref, Ref } from 'vue'

export function useTabTrap(modalRef: Ref<HTMLElement | null>, autoFocus: boolean = false) {
  const focusableElements = ref<HTMLElement[]>([])

  const getFocusableElements = (element: HTMLElement): HTMLElement[] => {
    return Array.from(
      element.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null)
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (!modalRef.value || e.key !== 'Tab') return

    const elements = focusableElements.value
    if (elements.length === 0) return

    const firstElement = elements[0]
    const lastElement = elements[elements.length - 1]
    const activeElement = document.activeElement

    if (e.shiftKey) {
      // If shift + tab and we're on the first element, move to the last
      if (activeElement === firstElement) {
        e.preventDefault()
        lastElement.focus()
      }
    } else {
      // If tab and we're on the last element, move to the first
      if (activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    }
  }

  const initTabTrap = () => {
    if (!modalRef.value) return

    // Get all focusable elements
    focusableElements.value = getFocusableElements(modalRef.value)

    // Focus the first element only if autoFocus is true
    if (autoFocus && focusableElements.value.length > 0) {
      focusableElements.value[0].focus()
    }

    // Add keydown listener
    document.addEventListener('keydown', handleKeydown)
  }

  const cleanupTabTrap = () => {
    document.removeEventListener('keydown', handleKeydown)
  }

  onMounted(() => {
    initTabTrap()
  })

  onBeforeUnmount(() => {
    cleanupTabTrap()
  })

  return {
    initTabTrap,
    cleanupTabTrap
  }
}
